import { safeNext } from "../../lib/safeNext";
import { AuthError, type AuthPort, type Consents, type Session } from "./port";

/**
 * Cognito Managed Login — authorization code + PKCE.
 *
 * Hand-rolled against the OAuth endpoints rather than pulled in via Amplify:
 * `src/test/no-aws.test.ts` forbids an `@aws-sdk/*` import, and the whole of
 * what we need is three fetches. Amplify would add ~200kB and a second opinion
 * about session storage.
 *
 * ═══ THE FIVE THINGS THAT MAKE THIS SAFE, AND WHY EACH IS HERE ═══
 *
 * 1. PKCE (S256). The code alone is useless without the verifier, which never
 *    leaves this origin. Without it, anyone who can observe the redirect URL —
 *    browser history, a proxy log, a Referer header — can exchange the code
 *    themselves. This is the control that makes a public client safe at all.
 *
 * 2. `state`, compared on return. A callback we did not initiate is refused.
 *    This is what stops login-CSRF: an attacker sending you to a callback URL
 *    carrying THEIR code, silently logging you into their account, and then
 *    reading whatever you do next.
 *
 * 3. `nonce`, compared against the ID token claim. Binds the returned token to
 *    this specific authorization request, closing token-replay.
 *
 * 4. The URL is scrubbed before anything else happens. `code` and `state` are
 *    removed with history.replaceState the moment we have them, so they are not
 *    in the address bar, not in the back-stack, and not in whatever the next
 *    outbound request puts in Referer.
 *
 * 5. THE ID TOKEN IS THE BEARER, NOT THE ACCESS TOKEN. This is the one that
 *    silently breaks. Two independent reasons:
 *      - The API Gateway JWT authorizer pins `jwtAudience: [clientId]`. Cognito
 *        ACCESS tokens have no `aud` claim at all — they carry `client_id` —
 *        so an access token fails that authorizer.
 *      - `email_verified` exists only in the ID token, and §7.2a requires it to
 *        be checked on EVERY request, not just at sign-in.
 *    kev-aws-setup-guide.md flags this exact migration as a silent breakage.
 *    Sending the access token "because that is what access tokens are for"
 *    produces a 401 wall with a completely clean auth log.
 *
 * ═══ WHERE TOKENS LIVE ═══
 *
 * Access/ID tokens: in memory, plus sessionStorage so a reload (and the Stripe
 * round-trip) does not sign you out. Refresh token: sessionStorage too, and this
 * is a KNOWN GAP against kev-security-model.md, which wants it in an httpOnly
 * cookie. Getting there needs a server-side token-exchange endpoint on our own
 * origin — real work, not a flag. It is called out in the security checklist
 * rather than papered over here. sessionStorage over localStorage at least means
 * the token dies with the tab and is not shared across them.
 */

const DOMAIN = import.meta.env.VITE_COGNITO_DOMAIN ?? "";
const CLIENT_ID = import.meta.env.VITE_COGNITO_CLIENT_ID ?? "";
const SCOPES = "openid email";
const REDIRECT_URI = `${window.location.origin}/auth/callback`;

const K_VERIFIER = "kev.pkce.verifier";
const K_STATE = "kev.pkce.state";
const K_NONCE = "kev.pkce.nonce";
const K_NEXT = "kev.pkce.next";
const K_CONSENTS = "kev.pkce.consents";
const K_TOKENS = "kev.tokens";

interface Tokens {
  idToken: string;
  accessToken: string;
  refreshToken?: string;
  /** Epoch ms. */
  expiresAt: number;
}

interface IdClaims {
  sub: string;
  email?: string;
  email_verified?: boolean | string;
  nonce?: string;
  exp: number;
  "custom:dobVerified"?: string;
}

const listeners = new Set<(s: Session | null) => void>();
let tokens: Tokens | null = null;

function emit() {
  const s = sessionFrom(tokens);
  listeners.forEach((cb) => cb(s));
}

function sessionFrom(t: Tokens | null): Session | null {
  if (!t) return null;
  const c = decodeJwt<IdClaims>(t.idToken);
  if (!c) return null;
  return {
    userId: c.sub,
    email: c.email ?? "",
    // Cognito serialises this as a real boolean in the ID token, but API
    // Gateway stringifies claims — accept both rather than depend on which
    // layer you happen to be reading.
    emailVerified: c.email_verified === true || c.email_verified === "true",
    dobVerified: c["custom:dobVerified"] === "true",
  };
}

function loadTokens(): Tokens | null {
  try {
    const raw = sessionStorage.getItem(K_TOKENS);
    return raw ? (JSON.parse(raw) as Tokens) : null;
  } catch {
    return null;
  }
}

function saveTokens(t: Tokens | null) {
  tokens = t;
  try {
    if (t) sessionStorage.setItem(K_TOKENS, JSON.stringify(t));
    else sessionStorage.removeItem(K_TOKENS);
  } catch {
    /* storage disabled — the session simply won't survive a reload */
  }
}

export const cognitoAuthAdapter: AuthPort = {
  async restoreSession() {
    tokens = loadTokens();
    if (!tokens) return null;
    // Refresh eagerly on boot if we are inside the skew window, so the first
    // API call of the session isn't the one that discovers the token is stale.
    if (Date.now() > tokens.expiresAt - 60_000) {
      const refreshed = await refresh();
      if (!refreshed) {
        saveTokens(null);
        return null;
      }
    }
    return sessionFrom(tokens);
  },

  async beginSignIn({ intent, next, consents, idp }) {
    if (!DOMAIN || !CLIENT_ID) {
      throw new AuthError("unknown", "Sign-in is not configured for this build.");
    }

    const verifier = randomString(64);
    const state = randomString(32);
    const nonce = randomString(32);

    sessionStorage.setItem(K_VERIFIER, verifier);
    sessionStorage.setItem(K_STATE, state);
    sessionStorage.setItem(K_NONCE, nonce);
    // `next` is kept HERE as well as in `state`. The copy in state survives a
    // fresh tab; this copy is the one we trust, because it never left the
    // origin. They are cross-checked on return.
    sessionStorage.setItem(K_NEXT, next);
    if (consents) sessionStorage.setItem(K_CONSENTS, JSON.stringify(consents));
    else sessionStorage.removeItem(K_CONSENTS);

    const params = new URLSearchParams({
      client_id: CLIENT_ID,
      response_type: "code",
      scope: SCOPES,
      redirect_uri: REDIRECT_URI,
      state,
      nonce,
      code_challenge: await s256(verifier),
      code_challenge_method: "S256",
      // Cognito's own parameter name. With it, Managed Login is skipped entirely
      // and the browser lands on Google's consent screen; without it, the user
      // sees our branded form with a Google button on it. Both end at the same
      // /auth/callback with the same code exchange.
      ...(idp ? { identity_provider: idp } : {}),
    });

    // Managed Login's two entry points. /signup is the same flow with the
    // create-account form shown first — not a different grant.
    // A federated start ALWAYS uses /oauth2/authorize. Cognito's /signup path
    // renders its own create-account form, which is meaningless when the
    // credentials are collected by Google — and it ignores identity_provider,
    // so the button would silently fall back to email/password.
    const path = intent === "signup" && !idp ? "signup" : "oauth2/authorize";
    window.location.assign(`https://${DOMAIN}/${path}?${params}`);

    // Never resolves; the page is leaving. The promise type says so.
    return new Promise<never>(() => {});
  },

  async completeSignIn() {
    const url = new URL(window.location.href);
    const code = url.searchParams.get("code");
    const returnedState = url.searchParams.get("state");
    const oauthError = url.searchParams.get("error");

    const verifier = sessionStorage.getItem(K_VERIFIER);
    const expectedState = sessionStorage.getItem(K_STATE);
    const expectedNonce = sessionStorage.getItem(K_NONCE);
    const storedNext = sessionStorage.getItem(K_NEXT);
    const rawConsents = sessionStorage.getItem(K_CONSENTS);

    // Scrub BEFORE any await. If the exchange throws, or the user closes the
    // tab mid-flight, the code must already be out of the URL — otherwise it
    // sits in history and in the Referer of the next outbound request.
    window.history.replaceState({}, "", "/auth/callback");
    for (const k of [K_VERIFIER, K_STATE, K_NONCE, K_NEXT, K_CONSENTS]) {
      sessionStorage.removeItem(k);
    }

    if (oauthError) {
      throw new AuthError("callback-invalid", "Sign-in was cancelled or refused.");
    }
    if (!code || !verifier || !expectedState) {
      throw new AuthError("callback-invalid", "That sign-in link is not valid.");
    }
    // Constant-time-ish comparison is overkill for a value we generated, but the
    // comparison itself is not optional: without it any code from any source is
    // accepted, which is login-CSRF.
    if (returnedState !== expectedState) {
      throw new AuthError("callback-invalid", "That sign-in link is not valid.");
    }

    const t = await exchange({
      grant_type: "authorization_code",
      client_id: CLIENT_ID,
      code,
      redirect_uri: REDIRECT_URI,
      code_verifier: verifier,
    });

    const claims = decodeJwt<IdClaims>(t.idToken);
    if (!claims) throw new AuthError("callback-invalid", "That sign-in link is not valid.");
    // Binds this token to the authorize request we made. Without it a token
    // obtained for a different request could be replayed into this session.
    if (expectedNonce && claims.nonce !== expectedNonce) {
      throw new AuthError("callback-invalid", "That sign-in link is not valid.");
    }

    saveTokens(t);
    const session = sessionFrom(t);
    if (!session) throw new AuthError("unknown", "Sign-in did not complete.");

    // §7.2a's client-visible half. The pre-authentication trigger normally
    // refuses this server-side; if a token arrives unverified anyway, we refuse
    // to hold it rather than letting the user roam until the API 403s.
    if (!session.emailVerified) {
      saveTokens(null);
      throw new AuthError("email-unverified", "Email not verified.");
    }

    emit();
    return {
      session,
      // Re-validated even though we wrote it: it has been through sessionStorage
      // and, in the state parameter, through a URL. safeNext rejects anything
      // that isn't a same-origin absolute path.
      next: safeNext(storedNext),
      consents: rawConsents ? (JSON.parse(rawConsents) as Consents) : undefined,
    };
  },

  async logout() {
    saveTokens(null);
    emit();
    // Global sign-out at Cognito too, or the next "sign in" silently reuses the
    // Managed Login session cookie and the user appears unable to log out.
    if (DOMAIN && CLIENT_ID) {
      const params = new URLSearchParams({
        client_id: CLIENT_ID,
        logout_uri: `${window.location.origin}/`,
      });
      window.location.assign(`https://${DOMAIN}/logout?${params}`);
    }
  },

  async getAccessToken() {
    if (!tokens) tokens = loadTokens();
    if (!tokens) return null;
    if (Date.now() > tokens.expiresAt - 60_000) {
      const ok = await refresh();
      if (!ok) {
        saveTokens(null);
        emit();
        return null;
      }
    }
    // The ID token, deliberately. See point 5 in the module docstring — this
    // single line is the difference between a working API and a 401 wall.
    return tokens!.idToken;
  },

  async refreshSession() {
    if (!tokens) tokens = loadTokens();
    if (!tokens) return null;
    // Unconditional — not "if near expiry". A perfectly valid token is exactly
    // the case this exists for: the account changed, the token did not.
    const ok = await refresh();
    if (!ok) {
      // The refresh token is gone or rejected, which means signed out. Say so
      // rather than returning the stale session we happen to still hold.
      saveTokens(null);
      emit();
      return null;
    }
    emit();
    return sessionFrom(tokens);
  },

  onSessionChange(cb) {
    listeners.add(cb);
    return () => listeners.delete(cb);
  },

  async resendVerification() {
    // Managed Login owns this screen: Cognito re-sends the code from its own
    // "confirm your account" page. There is no unauthenticated API for it that
    // does not also become an email-bombing endpoint, so we send the user back
    // into the hosted flow rather than building one.
    await cognitoAuthAdapter.beginSignIn({ intent: "login", next: "/app" });
  },
};

// ── token endpoint ────────────────────────────────────────────────────────────
/**
 * How long the token endpoint gets before we call it dead.
 *
 * THIS EXISTS BECAUSE ITS ABSENCE TOOK THE WHOLE APP DOWN. `fetch` has no
 * default timeout. A request that hangs — rather than failing — never settles,
 * so `refresh()` never returns, `restoreSession()` never resolves, and
 * AuthProvider's `status` stays "loading" for as long as the tab is open. Every
 * guarded route then renders LOADING… forever, with no error, no retry and
 * nothing on screen to act on.
 *
 * It presented as an account-specific bug, which is what made it hard to see:
 * `restoreSession` only calls the token endpoint when the stored token is
 * within a minute of expiring, so an account signed in constantly has a fresh
 * token, skips the network entirely and always boots. Only the account left
 * sitting long enough to go stale ever reaches this line.
 *
 * Ten seconds: long enough for a bad mobile connection, short enough that a
 * punter is looking at a sign-in page rather than a dead one.
 */
const TOKEN_TIMEOUT_MS = 10_000;

async function exchange(body: Record<string, string>): Promise<Tokens> {
  let res: Response;
  const ctl = new AbortController();
  const timer = setTimeout(() => ctl.abort(), TOKEN_TIMEOUT_MS);
  try {
    res = await fetch(`https://${DOMAIN}/oauth2/token`, {
      method: "POST",
      headers: { "content-type": "application/x-www-form-urlencoded" },
      body: new URLSearchParams(body).toString(),
      signal: ctl.signal,
    });
  } catch {
    // Covers the abort as well as a genuine network failure, and deliberately:
    // both mean "we did not get tokens", and both are recoverable by sending the
    // user to sign in again. `refresh()` catches this and returns false, which
    // clears the stored tokens and lands on the login page — a state someone can
    // get out of, unlike the hang.
    //
    // The other likely cause in production is our own CSP: connect-src must name
    // the Cognito domain or this fetch is blocked while the redirect half of
    // the flow works perfectly. See kev-edge-stack.ts.
    throw new AuthError("network", "Could not reach the sign-in service.");
  } finally {
    clearTimeout(timer);
  }
  if (!res.ok) throw new AuthError("callback-invalid", "That sign-in link is not valid.");

  const json = (await res.json()) as {
    id_token: string;
    access_token: string;
    refresh_token?: string;
    expires_in: number;
  };
  return {
    idToken: json.id_token,
    accessToken: json.access_token,
    refreshToken: json.refresh_token ?? tokens?.refreshToken,
    expiresAt: Date.now() + json.expires_in * 1000,
  };
}

/**
 * In-flight refresh, shared by every caller that arrives while one is running.
 *
 * WHY THIS IS NEEDED. `client.ts` calls `getAccessToken()` on EVERY
 * non-anonymous request, and react-query fires several at once — landing on
 * /app/season asks for the season board, entitlements and /me together. If the
 * stored token has drifted into the 60-second skew window by then, each of those
 * independently decides to refresh, and three identical `refresh_token` grants
 * go out for one expiry.
 *
 * Today that is merely wasteful: Cognito does not rotate refresh tokens by
 * default, so all three succeed and the last write wins. It stops being
 * harmless the moment **refresh token rotation** is switched on at the pool —
 * then the first grant invalidates the token the other two are still holding,
 * two of the three fail, `refresh()` returns false, and the app signs out a user
 * who did nothing but open a page. That is a configuration change in a console,
 * made by someone who has no reason to connect it to this file.
 *
 * The window is also the one this adapter has already been bitten in: a session
 * left sitting long enough to go stale is exactly the case that never comes up
 * in development, which is why the missing fetch timeout above went unnoticed
 * until it took the whole app down.
 */
let inFlight: Promise<boolean> | null = null;

async function refresh(): Promise<boolean> {
  /* Not `if (inFlight) return inFlight` inside the body below — the assignment
     has to happen before the first await, or two synchronous callers both see
     null and both start one. */
  if (inFlight) return inFlight;

  inFlight = doRefresh().finally(() => {
    inFlight = null;
  });
  return inFlight;
}

async function doRefresh(): Promise<boolean> {
  const rt = tokens?.refreshToken;
  if (!rt) return false;
  try {
    saveTokens(
      await exchange({ grant_type: "refresh_token", client_id: CLIENT_ID, refresh_token: rt }),
    );
    return true;
  } catch {
    return false;
  }
}

// ── PKCE primitives ───────────────────────────────────────────────────────────
function randomString(bytes: number): string {
  const a = new Uint8Array(bytes);
  crypto.getRandomValues(a);   // never Math.random — this is a security value
  return base64Url(a);
}

async function s256(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier));
  return base64Url(new Uint8Array(digest));
}

function base64Url(bytes: Uint8Array): string {
  let s = "";
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/**
 * Reads claims WITHOUT verifying the signature — and that is correct here.
 *
 * The token came straight from Cognito's token endpoint over TLS, so this is
 * decoding our own response, not trusting an attacker's input. The verification
 * that matters happens server-side: the API Gateway JWT authorizer checks the
 * signature, issuer, audience and expiry on every single request, and nothing
 * this function returns is ever used as an access-control decision — only to
 * decide what the UI renders.
 */
function decodeJwt<T>(jwt: string): T | null {
  try {
    const [, payload] = jwt.split(".");
    return JSON.parse(
      atob(payload.replace(/-/g, "+").replace(/_/g, "/")),
    ) as T;
  } catch {
    return null;
  }
}
